import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

gsap.registerPlugin(ScrollTrigger, SplitText);
gsap.ticker.lagSmoothing(0);
if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
scrollTo(0, 0);

const $ = (s) => document.querySelector(s);
addEventListener('error', e => { const t = $('#tag'); t.style.cssText = 'font-size:12px;color:#f0a94a;white-space:pre-wrap;max-width:90vw;letter-spacing:0;text-transform:none;bottom:40px'; t.textContent = 'ERR ' + (e.error && e.error.stack || e.message).slice(0, 600); });

/* ---------- catalogue (from forgelab.uk) ---------- */
/* the shelf, in order; names, prices and links come from products.py at build time */
const PRODUCTS = [{"key": "blok", "slug": "blok", "name": "BLOK", "led": {"rgb": true, "warm": true}}, {"key": "jrgb", "slug": "koshi", "name": "KOSHI", "led": {"rgb": true, "warm": true}}, {"key": "shoji", "slug": "shoji", "name": "SHOJI", "led": {"rgb": true, "warm": true}}, {"key": "pine", "slug": "matsu", "name": "MATSU", "led": {"rgb": true, "warm": true}}];
const HERO_KEY = 'jrgb';
const REVIEWS = [
  { q: 'Unique style table lamp, arrived quickly, seller allowed us to customise it too, great communication, perfect!', who: 'Graham', date: 'Mar 2026', prod: 'Japanese RGB Lamp' },
  { q: 'Great lamp, it was exactly as advertised on the picture. I got it in black. It comes with 3 shades of white/yellow which is great to create an atmosphere.', who: 'Méline', date: 'Jun 2026', prod: 'Japanese RGB Table Lamp · Black' },
  { q: 'Item arrived on time. Looks like the photo and I love the different colour lights.', who: 'April', date: 'Mar 2026', prod: 'Japanese RGB Table Lamp' },
  { q: 'Really good. Perfect for necron 40k army. Helpful seller. Had an issue and they resolved it. Plan to buy more. Excellent packing.', who: 'Chris', date: 'May 2026', prod: 'Crystal Lamp' },
  { q: 'Nice product, I like it.', who: 'Daniel', date: 'Mar 2026', prod: 'Crystal Lamp' },
  { q: 'Thanks, I appreciate it :)', who: 'Dan', date: 'Feb 2026', prod: 'Crystal Lamp' },
];

/* ---------- DOM ---------- */
const slidesEl = $('#slides'), dotsEl = $('#pdots');
const revsEl = $('#revs');
REVIEWS.forEach(r => {
  const d = document.createElement('div'); d.className = 'rev';
  d.innerHTML = `<q>${r.q}</q><div class="who"><b>${r.who}</b> · Etsy buyer<br><span>${r.date} · ${r.prod}</span></div>`;
  revsEl.appendChild(d);
});

/* ---------- WebGL guard ---------- */
const canvas = $('#scene');
const isMobile = matchMedia('(pointer:coarse)').matches || screen.width < 768;
const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
let W = Math.max(innerWidth, 320), H = Math.max(innerHeight, 240);
const probe = document.createElement('canvas');
const hasGL = !!(probe.getContext('webgl2') || probe.getContext('webgl')) && 'DecompressionStream' in window;
if (!hasGL) { canvas.style.display = 'none'; $('#nowebgl').classList.add('show'); }

const state = { power: 0, camAngle: 0, camDist: 8.0, camHeight: 2.0, lampX: 0, camX: 0, offX: 0, lookY: 1.25, explode: 0, clipY: 3.2, nozzle: 0, workLight: 0, roomDim: 0, shelf: 0, rgb: 0 };
let lit = false;

function staticFallback() {
  document.body.classList.add('nogl');
  $('#nav').classList.add('on'); $('#hint').classList.add('gone');
  document.querySelectorAll('#herocopy .eyebrow,#herocopy p,#herocopy .cta,#scrollcue').forEach(el => el.style.opacity = 1);
  document.querySelectorAll('.sec,.rev').forEach(el => el.classList.add('on'));
  $('#ledmode').classList.add('show');
  document.querySelectorAll('a[href^="#"]').forEach(a => a.addEventListener('click', e => { const el = $(a.getAttribute('href')); if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth' }); } }));
}
if (!hasGL) staticFallback(); else await main();

async function main() {

/* ---------- embedded meshes (the client's real STL files, quantised + gzipped) ---------- */
const MESHES = await (await fetch(window.FORGELAB.meshes, { cache: 'force-cache' })).json();
const MM = 0.01; // scene unit = 10 cm
async function unpackMesh(b64) {
  const bin = atob(b64); const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  const buf = await new Response(new Blob([u8]).stream().pipeThrough(new DecompressionStream('gzip'))).arrayBuffer();
  const hl = new DataView(buf).getUint32(0, true);
  const hdr = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 4, hl)));
  let off = 4 + hl;
  const q = new Uint16Array(buf.slice(off, off + hdr.nv * 6)); off += hdr.nv * 6;
  const idx = hdr.i32 ? new Uint32Array(buf.slice(off, off + hdr.nt * 12)) : new Uint16Array(buf.slice(off, off + hdr.nt * 6));
  const pos = new Float32Array(hdr.nv * 3);
  for (let i = 0; i < hdr.nv * 3; i++) pos[i] = hdr.min[i % 3] + q[i] / 65535 * hdr.ext[i % 3];
  return { pos, idx, hdr };
}
/* STL is z-up in mm; the scene is y-up. `pick(cx,cy,cz)` (STL mm) chooses which triangles go in. */
function toGeo(m, { zUp = true, pick = null, floor = true, centre = true } = {}) {
  const src = m.pos, n = m.hdr.nv;
  const pos = new Float32Array(n * 3);
  let minY = Infinity, minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (let i = 0; i < n; i++) {
    const x = src[i * 3] * MM, y = src[i * 3 + 1] * MM, z = src[i * 3 + 2] * MM;
    const X = x, Y = zUp ? z : y, Z = zUp ? -y : z;
    pos[i * 3] = X; pos[i * 3 + 1] = Y; pos[i * 3 + 2] = Z;
    if (Y < minY) minY = Y; if (X < minX) minX = X; if (X > maxX) maxX = X; if (Z < minZ) minZ = Z; if (Z > maxZ) maxZ = Z;
  }
  const dx = centre ? (minX + maxX) / 2 : 0, dz = centre ? (minZ + maxZ) / 2 : 0, dy = floor ? minY : 0;
  for (let i = 0; i < n; i++) { pos[i * 3] -= dx; pos[i * 3 + 1] -= dy; pos[i * 3 + 2] -= dz; }
  let idx = m.idx;
  if (pick) {
    const out = []; const s = src;
    for (let t = 0; t < idx.length; t += 3) {
      const a = idx[t] * 3, b = idx[t + 1] * 3, c = idx[t + 2] * 3;
      if (pick((s[a] + s[b] + s[c]) / 3, (s[a + 1] + s[b + 1] + s[c + 1]) / 3, (s[a + 2] + s[b + 2] + s[c + 2]) / 3)) out.push(idx[t], idx[t + 1], idx[t + 2]);
    }
    idx = n > 65535 ? new Uint32Array(out) : new Uint16Array(out);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setIndex(new THREE.BufferAttribute(idx, 1));
  g.computeVertexNormals(); g.computeBoundingBox();
  return g;
}
const M = {};
await Promise.all(Object.keys(MESHES).map(async k => { M[k] = await unpackMesh(MESHES[k]); }));

/* ---------- renderer / scene / camera ---------- */
const renderer = new THREE.WebGLRenderer({ canvas, antialias: !isMobile, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(devicePixelRatio, 1.25));
renderer.setSize(W, H);
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.1;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFShadowMap;
renderer.localClippingEnabled = true;
renderer.debug.checkShaderErrors = false; // avoids synchronous compile-status stalls on Windows/ANGLE
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x141210);
const camera = new THREE.PerspectiveCamera(34, W / H, 0.1, 80);

/* ---------- procedural textures ---------- */
function woodTexture() {
  const c = document.createElement('canvas'); c.width = c.height = 512;
  const g = c.getContext('2d');
  g.fillStyle = '#2a1c11'; g.fillRect(0, 0, 512, 512);
  for (let p = 0; p < 8; p++) {
    const y = p * 64;
    g.fillStyle = `hsl(${22 + Math.random() * 6},${38 + Math.random() * 10}%,${11 + Math.random() * 4}%)`;
    g.fillRect(0, y, 512, 62);
    for (let i = 0; i < 40; i++) {
      g.strokeStyle = `rgba(0,0,0,${0.08 + Math.random() * 0.18})`; g.lineWidth = 0.6 + Math.random() * 1.2;
      g.beginPath(); const yy = y + Math.random() * 62; g.moveTo(0, yy);
      for (let x = 0; x <= 512; x += 32) g.lineTo(x, yy + Math.sin(x * 0.02 + i) * 2.2);
      g.stroke();
    }
    g.fillStyle = 'rgba(0,0,0,.55)'; g.fillRect(0, y + 62, 512, 2);
  }
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(8, 8); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4; return t;
}
function woodRough() {
  const c = document.createElement('canvas'); c.width = c.height = 256; const g = c.getContext('2d');
  g.fillStyle = '#777'; g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 1400; i++) { const v = 80 + Math.random() * 110; g.fillStyle = `rgb(${v},${v},${v})`; g.fillRect(Math.random() * 256, Math.random() * 256, 1 + Math.random() * 40, 1); }
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(8, 8); return t;
}
function layerBump() {
  const c = document.createElement('canvas'); c.width = 4; c.height = 64; const g = c.getContext('2d');
  for (let y = 0; y < 64; y++) { const v = 120 + Math.round(Math.sin(y / 64 * Math.PI * 8) * 60); g.fillStyle = `rgb(${v},${v},${v})`; g.fillRect(0, y, 4, 1); }
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(1, 26); return t;
}
function plasterTexture() {
  const c = document.createElement('canvas'); c.width = c.height = 256; const g = c.getContext('2d');
  g.fillStyle = '#2b2520'; g.fillRect(0, 0, 256, 256);
  const img = g.getImageData(0, 0, 256, 256);
  for (let i = 0; i < img.data.length; i += 4) { const n = (Math.random() - 0.5) * 14; img.data[i] += n; img.data[i + 1] += n; img.data[i + 2] += n; }
  g.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(10, 3); t.colorSpace = THREE.SRGBColorSpace; return t;
}
function paperGrain() {
  const c = document.createElement('canvas'); c.width = c.height = 256; const g = c.getContext('2d');
  g.fillStyle = '#efe0c4'; g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 2600; i++) { g.fillStyle = `rgba(120,80,40,${Math.random() * 0.09})`; g.fillRect(Math.random() * 256, Math.random() * 256, 1 + Math.random() * 6, 1); }
  for (let y = 0; y < 256; y += 3) { g.fillStyle = 'rgba(0,0,0,.05)'; g.fillRect(0, y, 256, 1); }
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(3, 3); t.colorSpace = THREE.SRGBColorSpace; return t;
}

const bump = layerBump();
/* ---------- room ---------- */
const floor = new THREE.Mesh(new THREE.PlaneGeometry(40, 24), new THREE.MeshStandardMaterial({ map: woodTexture(), roughnessMap: woodRough(), roughness: 0.55, metalness: 0.04 }));
floor.rotation.x = -Math.PI / 2; floor.receiveShadow = true; scene.add(floor);
const wall = new THREE.Mesh(new THREE.PlaneGeometry(40, 8), new THREE.MeshStandardMaterial({ map: plasterTexture(), roughness: 0.94 }));
wall.position.set(0, 4, -1.9); wall.receiveShadow = true; scene.add(wall);
const win = new THREE.Group();
win.add(new THREE.Mesh(new THREE.PlaneGeometry(1.5, 2.1), new THREE.MeshBasicMaterial({ color: 0x0b1220 })));
const winMat = new THREE.MeshStandardMaterial({ color: 0x1a1512, roughness: 0.8, bumpMap: bump, bumpScale: 0.001, flatShading: true, side: THREE.DoubleSide });
[[0, 0, 1.6, 0.06], [0, 0, 0.06, 2.2], [0, 1.08, 1.6, 0.06], [0, -1.08, 1.6, 0.06], [0.78, 0, 0.06, 2.2], [-0.78, 0, 0.06, 2.2]].forEach(([x, y, w, h]) => {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.05), winMat); m.position.set(x, y, 0.03); win.add(m);
});
win.position.set(-3.1, 2.4, -1.88); scene.add(win);

/* ---------- lights ---------- */
const hemi = new THREE.HemisphereLight(0x4a4440, 0x14100c, 0.12); scene.add(hemi);
/* a soft key light that rides with the camera, so the frames and lattices read as shapes and not as silhouettes */
const keyLight = new THREE.DirectionalLight(0xd9c7a8, 0.3); keyLight.target.position.set(0, 1, 0); scene.add(keyLight, keyLight.target);
/* a little moonlight through the window so the unlit lamp still has a silhouette */
const moon = new THREE.DirectionalLight(0x6f86b8, 0.3); moon.position.set(-3.1, 3.4, 1.4); moon.target.position.set(0, 1, 0); scene.add(moon, moon.target);
/* Warm (non-RGB) LED colour temperatures: 3000 K warm white, 4000 K neutral, 6500 K daylight */
const KELVIN = { 3000: { hex: 0xffb872, name: 'Warm', txt: 'Warm white, 3000 K, like an old incandescent bulb. The cosiest of the three: evenings, bedside, a dark corner.' },
                 4000: { hex: 0xffd9ad, name: 'Neutral', txt: 'Neutral white, 4000 K. Balanced, neither yellow nor blue: a desk, a kitchen shelf, a hallway.' },
                 6500: { hex: 0xeef4ff, name: 'Daylight', txt: 'Daylight, 6500 K. Crisp and bright, closest to midday light: reading, a workshop, a bright room.' } };
const WARM = new THREE.Color(KELVIN[3000].hex);
const warmCol = WARM.clone(); // the currently chosen temperature (tweened)
const led = new THREE.PointLight(WARM, 0, 16, 2);
led.position.set(0, 1.05, 0); led.castShadow = true;
led.shadow.mapSize.set(isMobile ? 512 : 768, isMobile ? 512 : 768); led.shadow.bias = -0.0015; led.shadow.normalBias = 0.015; led.shadow.radius = 3;
/* soft fill: the diffuser is an area light in reality, a second shadowless point light fakes its softness */
const fill = new THREE.PointLight(WARM, 0, 5, 2);
const mouseLight = new THREE.PointLight(0xd9c7a8, 0, 6, 2); mouseLight.position.set(0, 1.4, 1.4); scene.add(mouseLight);
const work = new THREE.SpotLight(0xc2d3ee, 0, 14, 0.42, 0.7, 2);
work.position.set(0.6, 4.6, 1.8); work.target.position.set(0, 0.6, 0); scene.add(work, work.target);

/* ---------- materials ---------- */
const clipPlane = new THREE.Plane(new THREE.Vector3(0, -1, 0), state.clipY);
const grain = paperGrain();
/* translucent PLA: emissive falls off with distance from the LED, so the diffuser has a hot spot near the bulb and fades towards the edges */
function glowShader(mat, u) {
  mat.userData.u = u;
  mat.onBeforeCompile = sh => {
    sh.uniforms.uLed = u.uLed; sh.uniforms.uHot = u.uHot; sh.uniforms.uSpread = u.uSpread;
    sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\nvarying vec3 vWp;').replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvWp = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    sh.fragmentShader = sh.fragmentShader.replace('#include <common>', '#include <common>\nvarying vec3 vWp; uniform vec3 uLed; uniform float uHot; uniform float uSpread;')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\n{ float dL = distance(vWp, uLed) * uSpread; totalEmissiveRadiance *= (0.38 + uHot / (1.0 + dL * dL)); }');
  };
  mat.customProgramCacheKey = () => 'glow';
  return mat;
}
function mkPaper(clipped) {
  const m = new THREE.MeshStandardMaterial({ color: 0xf4e3c2, map: grain, emissive: WARM.clone(), emissiveIntensity: 0, roughness: 0.85, side: THREE.DoubleSide, transparent: true, opacity: 0.97, clippingPlanes: clipped ? [clipPlane] : null, clipShadows: !!clipped });
  return glowShader(m, { uLed: { value: new THREE.Vector3(0, 1, 0) }, uHot: { value: 1.4 }, uSpread: { value: 1.6 } });
}
const plaMat = new THREE.MeshStandardMaterial({ color: 0x2c2724, roughness: 0.6, metalness: 0.02, bumpMap: bump, bumpScale: 0.006, flatShading: true, side: THREE.DoubleSide, clippingPlanes: [clipPlane], clipShadows: true });
const paperMat = mkPaper(true);
const ledMat = plaMat.clone(); ledMat.color.set(0xfff1d6); ledMat.emissive.copy(WARM); ledMat.roughness = 0.4; ledMat.bumpScale = 0;
const pcbMat = plaMat.clone(); pcbMat.color.set(0x14161a); pcbMat.roughness = 0.5; pcbMat.metalness = 0.3; pcbMat.bumpScale = 0;
const plaFree = plaMat.clone(); plaFree.clippingPlanes = null; plaFree.clipShadows = false;
const woodMat = plaFree.clone(); woodMat.color.set(0x6b4a2e); woodMat.roughness = 0.7;
const pineMat = plaFree.clone(); pineMat.color.set(0x4a3322); pineMat.roughness = 0.65;

/* ---------- hero lamp: the Japanese RGB Table Lamp, from the client's STL ---------- */
const lamp = new THREE.Group(); scene.add(lamp);
const parts = []; const ref = {};
function part(obj, home, out, name) {
  obj.position.copy(home); obj.userData.home = home.clone(); obj.userData.out = out.clone();
  lamp.add(obj); parts.push(obj); if (name) ref[name] = obj; return obj;
}
const heroFrameGeo = toGeo(M.hero, { pick: (x, y, z) => !(Math.abs(x) < 44.8 && Math.abs(y) < 44.8 && z > 73 && z < 249) });
const heroDiffGeo = toGeo(M.hero, { pick: (x, y, z) => Math.abs(x) < 44.8 && Math.abs(y) < 44.8 && z > 73 && z < 249 });
const HERO_H = heroFrameGeo.boundingBox.max.y; // ≈ 2.34
const frame = new THREE.Mesh(heroFrameGeo, plaMat); frame.castShadow = true; frame.receiveShadow = true;
part(frame, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0.95, 0), 'frame');
const grille = new THREE.Object3D(); grille.position.set(0.25, HERO_H, 0.25); frame.add(grille); ref.grille = grille;
const foot = new THREE.Object3D(); foot.position.set(-0.45, 0.12, 0.45); frame.add(foot); ref.base = foot;
const diffuser = new THREE.Mesh(heroDiffGeo, paperMat); diffuser.castShadow = false;
part(diffuser, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0.9, 0.35, 0.55), 'diffuser');
const ledGroup = new THREE.Group();
const pcb = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.06, 0.34), pcbMat); pcb.position.y = 0.76; ledGroup.add(pcb);
const bulb = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 0.42, 24), ledMat); bulb.position.y = 1.0; ledGroup.add(bulb);
led.position.set(0, 1.1, 0); ledGroup.add(led);
fill.position.set(0, 1.55, 0); ledGroup.add(fill);
part(ledGroup, new THREE.Vector3(0, 0, 0), new THREE.Vector3(-0.85, -0.25, 0.65), 'led'); ref.led = bulb;
const cableCurve = new THREE.CatmullRomCurve3([new THREE.Vector3(0.1, 0.05, 0.5), new THREE.Vector3(0.25, 0.03, 1.05), new THREE.Vector3(0.95, 0.02, 1.7), new THREE.Vector3(1.9, 0.02, 2.05)]);
const cableMat = plaFree.clone(); cableMat.color.set(0x1c1c1e); cableMat.bumpScale = 0;
const cable = new THREE.Mesh(new THREE.TubeGeometry(cableCurve, 64, 0.028, 18, false), cableMat);
cable.castShadow = true;
const cableAnchor = new THREE.Object3D(); cableAnchor.position.copy(cableCurve.getPoint(0.4)); cable.add(cableAnchor);
part(cable, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0.35, 0.4, 0.45), 'cable'); ref.cable = cableAnchor;
const remote = new THREE.Group();
const rMat = plaFree.clone(); rMat.color.set(0xa39d92); rMat.roughness = 0.7;
const rBody = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.04, 0.78), rMat); rBody.castShadow = true; remote.add(rBody);
for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) {
  const km = plaFree.clone(); km.color.set([0xd23b3b, 0x3bb75a, 0x3b6fd2, 0xf2efe6][c]);
  const k = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.02, 12), km);
  k.position.set(-0.11 + c * 0.073, 0.03, -0.3 + r * 0.12); remote.add(k);
}
remote.position.set(1.75, 0.02, 0.75); remote.rotation.y = 0.35; scene.add(remote);

/* printing rig */
const nozzle = new THREE.Group();
const nzMat = plaFree.clone(); nzMat.color.set(0xb8b1a4); nzMat.roughness = 0.35; nzMat.metalness = 0.8;
const cone = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.2, 20), nzMat);
cone.rotation.x = Math.PI; cone.position.y = 0.12; nozzle.add(cone);
const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.5, 12), nzMat); shaft.position.y = 0.47; nozzle.add(shaft);
const tip = new THREE.Mesh(new THREE.SphereGeometry(0.022, 10, 10), new THREE.MeshBasicMaterial({ color: new THREE.Color(3.2, 1.7, 0.6), toneMapped: false })); tip.position.y = 0.015; nozzle.add(tip);
nozzle.visible = false; scene.add(nozzle);
const RING = 0.58;
const ring = new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(-RING, 0, -RING), new THREE.Vector3(RING, 0, -RING), new THREE.Vector3(RING, 0, RING), new THREE.Vector3(-RING, 0, RING)]), new THREE.LineBasicMaterial({ color: new THREE.Color(2.6, 1.4, 0.5), toneMapped: false, transparent: true, opacity: 0.9 }));
ring.visible = false; scene.add(ring);

/* ---------- the shelf: the other lamps ---------- */
const SPACING = 3.4;
const shelf = []; // {group, x, glowMats, light, key}
function mkLamp(key, x) {
  const g = new THREE.Group(); g.position.x = x; g.visible = false; scene.add(g);
  const glowMats = []; const paper = mkPaper(false); glowMats.push(paper);
  const light = { position: new THREE.Vector3(0, 1, 0), intensity: 0, color: WARM.clone(), distance: 10, rgb: true };
  const B = (w, h, d, mat, x = 0, y = 0, z = 0) => { const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat); m.position.set(x, y, z); m.castShadow = true; g.add(m); return m; };
  const STL = (m, mat, opts) => { const mesh = new THREE.Mesh(toGeo(m, opts), mat); mesh.castShadow = true; mesh.receiveShadow = true; g.add(mesh); return mesh; };
  if (key === 'blok') {
    /* client STL: engraved base (0–20 mm) + stem + hollow cube (66–266 mm) */
    STL(M.blok, woodMat, { pick: (x, y, z) => z < 66 });
    STL(M.blok, paper, { pick: (x, y, z) => z >= 66 }).castShadow = false;
    light.position.y = 1.66; light.distance = 11;
  } else if (key === 'shoji') {
    /* client STL: three-tier shoji. Inner box (|x|,|y| <= 50 mm, z 22-204) is the diffuser; posts, rails, top lattice and base are the frame.
       The frame is the warm grey of the product photo, not black, so it reads as a frame and not as a window. */
    const inner = (x, y, z) => Math.abs(x) < 50.5 && Math.abs(y) < 50.5 && z > 22 && z < 204;
    const frame = plaFree.clone(); frame.color.set(0x8a7f74); frame.roughness = 0.75;
    STL(M.shoji, frame, { pick: (x, y, z) => !inner(x, y, z) });
    STL(M.shoji, paper, { pick: inner }).castShadow = false;
    paper.userData.u.uHot.value = 1.1; paper.userData.u.uSpread.value = 2.2;
    light.position.y = 1.05;
  } else if (key === 'pine') {
    /* client STL: pagoda roof (z 194-306), base with feet (z < 24) and four 4 mm walls (|x| or |y| = 53..57 mm) with the lattice engraved 2 mm deep.
       The recessed panel areas (thin, o = 54-56 mm) glow as paper; the raised lattice bars (o = 57 mm), posts, rails, roof and base are dark PLA.
       No fake core box: the light sits inside the real walls. */
    const wall = (x, y, z) => { const o = Math.max(Math.abs(x), Math.abs(y)), i = Math.min(Math.abs(x), Math.abs(y)); return z > 30 && z < 188 && o > 53.8 && o < 56.4 && i < 49; };
    STL(M.pine, pineMat, { pick: (x, y, z) => !wall(x, y, z) });
    STL(M.pine, paper, { pick: wall }).castShadow = false;
    paper.userData.u.uHot.value = 1.2; paper.userData.u.uSpread.value = 2.4;
    light.position.y = 1.1; light.distance = 13;
  }
  glowMats.forEach(m => { if (m.userData.u) m.userData.u.uLed.value.copy(light.position).add(new THREE.Vector3(x, 0, 0)); });
  const o = { group: g, x, glowMats, light, key }; shelf.push(o); return o;
}
const shelfLight = new THREE.PointLight(WARM, 0, 10, 2); scene.add(shelfLight);
const ORDER = PRODUCTS.map(p => p.key);
const heroIdx = ORDER.indexOf(HERO_KEY);
const shelfX = {}; ORDER.forEach((k, i) => { shelfX[k] = (i - heroIdx) * SPACING; });
ORDER.forEach(k => { if (k !== HERO_KEY) mkLamp(k, shelfX[k]); });

/* precompile so the shelf doesn't stall when it appears */
shelf.forEach(o => { o.group.visible = true; o.group.position.y = -60; });
nozzle.visible = ring.visible = true;
const precompile = (renderer.compileAsync ? renderer.compileAsync(scene, camera) : Promise.resolve(renderer.compile(scene, camera))).catch(() => {});
precompile.then(() => { shelf.forEach(o => { o.group.visible = false; o.group.position.y = 0; }); nozzle.visible = ring.visible = false; });

/* ---------- post ---------- */
const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, camera));
const bloom = new UnrealBloomPass(new THREE.Vector2(W / 2, H / 2), 0, 0.55, 0.86);
composer.addPass(bloom); composer.addPass(new OutputPass());

/* ---------- DOM refs ---------- */
const root = document.documentElement;
const hint = $('#hint'), prog = $('#prog'), nav = $('#nav'), wordmark = $('#wordmark');
const labels = [...document.querySelectorAll('.lbl')];
const linesSvg = $('#lines');
labels.forEach(l => {
  const ln = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle'); dot.setAttribute('r', '2.2');
  linesSvg.append(ln, dot); l._line = ln; l._dot = dot;
});
const layerEl = $('#layer'), ptimeEl = $('#ptime');
const slideEls = [...document.querySelectorAll('.slide')], dotEls = [...dotsEl.children], pidx = $('#pidx');

/* ---------- LED mode: warm (5–10 W) vs RGB (1–3 W) ---------- */
const RGB_COLOURS = { red: 0xff3b2f, orange: 0xff8a1f, green: 0x35f06a, cyan: 0x25e6ff, blue: 0x2f6bff, purple: 0xb14dff, pink: 0xff4fd8, white: 0xf2f4ff };
const rgbCol = new THREE.Color(RGB_COLOURS.blue);
let cycling = false, cycleHue = 0.62;
const ledmode = $('#ledmode'), lmtxt = $('#lmtxt');
let kelvin = 3000;
const TXT = {
  get warm() { return KELVIN[kelvin].txt + ' 5–10 W: roughly three times the light of the RGB.'; },
  rgb: 'Mood light, 1–3 W. Sixteen colours, fades and brightness from the wireless remote. Softer: it colours the room rather than lighting it.'
};
function setMode(mode) {
  const rgb = mode === 'rgb';
  gsap.to(state, { rgb: rgb ? 1 : 0, duration: 0.9, ease: 'power2.inOut' });
  ledmode.classList.toggle('rgb', rgb);
  ledmode.querySelectorAll('.lm-opt').forEach(b => b.classList.toggle('on', b.dataset.mode === mode));
  lmtxt.textContent = TXT[mode];
  document.body.classList.toggle('rgbmode', rgb);
}
const mobileUI = matchMedia('(max-width:720px)');
ledmode.querySelectorAll('.lm-opt').forEach(b => b.addEventListener('click', () => {
  const same = b.classList.contains('on');
  if (mobileUI.matches) ledmode.classList.toggle('open', same ? !ledmode.classList.contains('open') : true);
  if (!same) setMode(b.dataset.mode);
}));
let scrollT = null;
addEventListener('scroll', () => { if (!mobileUI.matches) return; clearTimeout(scrollT); scrollT = setTimeout(() => ledmode.classList.remove('open'), 900); }, { passive: true });
const rgbW = ledmode.querySelector('.lm-opt[data-mode=rgb] .lm-w');
const kchips = $('#lmk');
const warmW = ledmode.querySelector('.lm-opt[data-mode=warm] .lm-w');
function setKelvin(k) {
  kelvin = k;
  const target = new THREE.Color(KELVIN[k].hex);
  gsap.to(warmCol, { r: target.r, g: target.g, b: target.b, duration: 0.7, ease: 'power2.inOut' });
  kchips.querySelectorAll('button').forEach(x => x.classList.toggle('on', +x.dataset.k === k));
  warmW.innerHTML = '5–10 W<span class="lm-now"> · ' + k + ' K</span>';
  if (ledmode.classList.contains('rgb')) setMode('warm'); else lmtxt.textContent = TXT.warm;
}
Object.entries(KELVIN).forEach(([k, v]) => {
  const b = document.createElement('button'); b.type = 'button'; b.dataset.k = k; b.title = v.name;
  b.innerHTML = `<i style="background:#${v.hex.toString(16).padStart(6, '0')}"></i>${k} K<small>${v.name}</small>`;
  if (+k === kelvin) b.classList.add('on');
  b.addEventListener('click', () => setKelvin(+k));
  kchips.appendChild(b);
});
warmW.innerHTML = '5–10 W<span class="lm-now"> · 3000 K</span>';
const chips = $('#lmchips');
Object.entries(RGB_COLOURS).forEach(([k, c]) => {
  const b = document.createElement('button'); b.type = 'button'; b.dataset.c = k; b.title = k; b.style.background = '#' + c.toString(16).padStart(6, '0');
  if (k === 'blue') b.classList.add('on');
  b.addEventListener('click', () => { cycling = false; rgbCol.set(c); chips.querySelectorAll('button').forEach(x => x.classList.toggle('on', x === b)); rgbW.textContent = '1–3 W · ' + k; if (!ledmode.classList.contains('rgb')) setMode('rgb'); });
  chips.appendChild(b);
});
const cyc = document.createElement('button'); cyc.type = 'button'; cyc.className = 'cycle'; cyc.textContent = 'fade'; cyc.title = 'colour fade mode';
cyc.addEventListener('click', () => { cycling = true; chips.querySelectorAll('button').forEach(x => x.classList.toggle('on', x === cyc)); rgbW.textContent = '1–3 W · fade'; if (!ledmode.classList.contains('rgb')) setMode('rgb'); });
rgbW.textContent = '1–3 W · blue';
chips.appendChild(cyc);

/* ---------- ignition ---------- */
const split = new SplitText('#h1', { type: 'chars,words', charsClass: 'char' });
const reveal = gsap.timeline({ paused: true })
  .to('#herocopy .eyebrow', { opacity: 1, duration: 0.8 }, 0)
  .to(split.chars, { opacity: 1, color: '#EFE6D2', duration: 0.05, stagger: { each: 0.028 }, ease: 'none' }, 0.1)
  .to('#herocopy p', { opacity: 1, duration: 0.9 }, 0.7)
  .to('#herocopy .cta', { opacity: 1, duration: 0.7 }, 1.0)
  .to('#scrollcue', { opacity: 1, duration: 0.8 }, 1.2)
  .add(() => ledmode.classList.add('show'), 1.1);
let holdTween = null;
function ignite(fast) {
  if (lit) return; lit = true;
  if (holdTween) holdTween.kill();
  holdTween = gsap.to(state, { power: 1, duration: fast ? 1.1 : 1.0, ease: 'power2.inOut', onComplete: onLit });
}
function onLit() { hint.classList.add('gone'); nav.classList.add('on'); reveal.play(); $('#hero').style.touchAction = 'auto'; }
function holdStart(e) {
  if (lit) return; if (e.target.closest('a,button')) return; e.preventDefault();
  if (holdTween) holdTween.kill();
  holdTween = gsap.to(state, { power: 1, duration: 1.7 * (1 - state.power), ease: 'power1.in', onComplete: () => { lit = true; onLit(); } });
}
function holdEnd() { if (lit) return; if (holdTween) holdTween.kill(); holdTween = gsap.to(state, { power: 0, duration: 0.9, ease: 'power2.out' }); }
const hero = $('#hero');
hero.addEventListener('pointerdown', holdStart);
hero.addEventListener('pointerup', holdEnd);
hero.addEventListener('pointercancel', holdEnd);
hero.addEventListener('pointerleave', holdEnd);
hint.addEventListener('keydown', e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); ignite(false); } });
addEventListener('wheel', () => ignite(true), { passive: true, once: true });
hero.addEventListener('touchmove', () => ignite(true), { passive: true, once: true });
gsap.timeline().to('#intro', { width: '50vw', duration: 1.1, ease: 'power2.inOut', delay: 0.3 }).to('#intro', { opacity: 0, duration: 0.5 }).add(() => hint.classList.add('show'), '-=0.3');
if (reduced) { state.power = 1; lit = true; setTimeout(onLit, 400); }

/* ---------- scroll journey, measured in px ---------- */
const PRINT_TOP = HERO_H + 0.06;
let tl = null;
const INIT = { ...state };
function buildTimeline() {
  const J = $('#journey');
  if (H < 50 || J.offsetHeight < 100 || $('#s2').offsetHeight < 10) return; // layout not ready: a zero-height build would snap the tweens to their end values
  if (tl) { tl.scrollTrigger && tl.scrollTrigger.kill(); tl.kill(); }
  const keep = { power: state.power, rgb: state.rgb };
  Object.assign(state, INIT, keep);
  const jTop = J.offsetTop;
  const S = id => { const el = $(id); return { a: el.offsetTop - jTop, h: el.offsetHeight }; };
  const s2 = S('#s2'), s3 = S('#s3'), s4 = S('#s4'), s5 = S('#s5'), rv = S('#reviews'), st = S('#story'), s8 = S('#s8');
  const total = J.offsetHeight;
  const vh = H;
  tl = gsap.timeline({ defaults: { ease: 'none' }, scrollTrigger: { trigger: J, start: 'top top', end: 'bottom bottom', scrub: 0.9, onEnter: () => ignite(true) } });
  // S2 with you
  tl.to(state, { camAngle: 0.55, camDist: 6.4, lampX: 0.4, offX: -1.3, ease: 'power2.inOut', duration: s2.h * 0.77 }, s2.a);
  // S3 anatomy
  tl.to(state, { lampX: 0, offX: -1.1, camAngle: 0.14, camDist: 8.6, camHeight: 2.2, ease: 'power2.inOut', duration: s3.h * 0.37 }, s3.a)
    .to(state, { explode: 1, ease: 'power2.inOut', duration: s3.h * 0.44 }, s3.a + s3.h * 0.28);
  // S4 forge
  const f = s4.h / 250;
  tl.to(state, { explode: 0, ease: 'power2.inOut', duration: 40 * f }, s4.a + 2 * f)
    .to(state, { offX: -0.9, camDist: 7.6, ease: 'power2.inOut', duration: 50 * f }, s4.a + 2 * f)
    .to(state, { power: 0, ease: 'power1.in', duration: 25 * f }, s4.a + 38 * f)
    .to(state, { clipY: -0.06, ease: 'power1.in', duration: 32 * f }, s4.a + 44 * f)
    .to(state, { workLight: 1, duration: 18 * f }, s4.a + 62 * f)
    .to(state, { clipY: PRINT_TOP, duration: 140 * f }, s4.a + 78 * f)
    .to(state, { nozzle: 52, duration: 140 * f }, s4.a + 78 * f)
    .to(state, { workLight: 0, duration: 14 * f }, s4.a + 218 * f)
    .to(state, { power: 1, ease: 'power3.out', duration: 18 * f }, s4.a + 224 * f)
    .to(state, { clipY: 6, duration: 4 * f }, s4.a + 244 * f);
  // S5 the shelf: camera walks along the lamps
  const first = shelfX[ORDER[0]], last = shelfX[ORDER[ORDER.length - 1]];
  tl.to(state, { shelf: 1, camAngle: 0.22, camDist: 7.4, camHeight: 2.0, lookY: 1.2, lampX: 0, offX: -1.0, camX: first, ease: 'power2.inOut', duration: vh * 0.9 }, s5.a - vh * 0.9)
    .to(state, { camX: last, duration: s5.h - vh }, s5.a);
  // reviews + story: the lamp steps aside, the shelf stays faint behind
  tl.to(state, { camX: 0, offX: 0, camAngle: -0.5, camDist: 11, camHeight: 3.2, lookY: 2.4, ease: 'power2.inOut', duration: vh }, rv.a - vh * 0.6)
    .to(state, { camAngle: 0.45, camDist: 9, offX: -1.6, lookY: 1.6, camHeight: 2.4, ease: 'power2.inOut', duration: vh }, st.a - vh * 0.6);
  // S8 finale
  tl.to(state, { camDist: 10.5, camHeight: 3.0, camAngle: -0.35, lampX: 0, camX: 0, offX: 0, lookY: 2.6, roomDim: 1, ease: 'power2.inOut', duration: vh }, s8.a - vh * 0.3);
  tl.set({}, {}, total - vh);
}
document.querySelectorAll('.sec').forEach(sec => ScrollTrigger.create({ trigger: sec, start: 'top 55%', end: 'bottom 45%', toggleClass: { targets: sec, className: 'on' } }));
ScrollTrigger.create({ trigger: '#s2', start: 'top 80%', onEnter: () => { wordmark.classList.add('away'); document.body.classList.add('sealon'); }, onLeaveBack: () => { wordmark.classList.remove('away'); document.body.classList.remove('sealon'); } });
ScrollTrigger.create({ trigger: '#s5', start: 'top 200%', onEnter: () => shelf.forEach(o => o.group.visible = true), onLeaveBack: () => shelf.forEach(o => o.group.visible = false) });
ScrollTrigger.create({ trigger: '#s5', start: 'top top', end: 'bottom bottom', snap: { snapTo: 1 / (PRODUCTS.length - 1), duration: { min: 0.2, max: 0.5 }, delay: 0.05, ease: 'power2.inOut' } });
ScrollTrigger.batch('.rev', { start: 'top 85%', onEnter: b => b.forEach((el, i) => setTimeout(() => el.classList.add('on'), i * 180)) });
buildTimeline();
window.addEventListener('load', () => { buildTimeline(); ScrollTrigger.refresh(); });
if (document.fonts) document.fonts.ready.then(() => { buildTimeline(); ScrollTrigger.refresh(); });

document.querySelectorAll('a[href^="#"]').forEach(a => a.addEventListener('click', e => { const el = $(a.getAttribute('href')); if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth' }); } }));

/* ---------- lights out ---------- */
let blackout = false, savedPower = 1;
$('#switch').addEventListener('click', () => {
  blackout = !blackout; document.body.classList.toggle('blackout', blackout);
  if (blackout) { savedPower = state.power; $('#gntime').textContent = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }).format(new Date()); gsap.to(state, { power: 0, duration: 1.6, ease: 'power2.out' }); }
  else gsap.to(state, { power: savedPower || 1, duration: 1.2, ease: 'power2.out' });
});
$('#goodnight').addEventListener('click', () => $('#switch').click());

/* ---------- mouse light ---------- */
const mouse = { x: 0, y: 0, active: false };
if (!isMobile) addEventListener('pointermove', e => { mouse.x = (e.clientX / W) * 2 - 1; mouse.y = -(e.clientY / H) * 2 + 1; mouse.active = true; });

/* ---------- loop ---------- */
const target = new THREE.Vector3(), tmp = new THREE.Vector3(), tmp2 = new THREE.Vector3();
const cur = new THREE.Color(), tmpC = new THREE.Color();
const clock = new THREE.Clock();
let running = true, lastLit = -1, curIdx = -1;
document.addEventListener('visibilitychange', () => { running = !document.hidden; if (running) tick(); });
function project(obj, out) { obj.getWorldPosition(tmp); tmp.project(camera); out.x = (tmp.x + 1) / 2 * W; out.y = (1 - tmp.y) / 2 * H; return out; }
function nozzlePos(u, s) {
  u = u - Math.floor(u); const side = Math.floor(u * 4), t = u * 4 - side;
  if (side === 0) return [-s + 2 * s * t, s]; if (side === 1) return [s, s - 2 * s * t]; if (side === 2) return [s - 2 * s * t, -s]; return [-s, -s + 2 * s * t];
}
/* adaptive quality: if the GPU can't keep up, step the pixel ratio down and turn the LED shadow off before the page feels sluggish */
let frames = 0, slowFrames = 0, lastT = 0, quality = 2;
const tagEl = $('#tag'); const DEBUG = /fps/.test(location.hash);
function degrade() {
  quality--;
  if (quality === 1) { renderer.setPixelRatio(1); composer.setPixelRatio && composer.setPixelRatio(1); renderer.setSize(W, H); composer.setSize(W, H); }
  if (quality === 0) { led.castShadow = false; led.shadow.map && led.shadow.map.dispose(); led.shadow.map = null; }
}
const FRAME_CAP = 0; // ms; 0 = uncapped (vsync)
let lastFrame = 0;
function tick() {
  if (!running) return;
  requestAnimationFrame(tick);
  if (FRAME_CAP) { const now = performance.now(); if (now - lastFrame < FRAME_CAP) return; lastFrame = now; }
  const t = clock.getElapsedTime(); const s = state;
  const dt = t - lastT; lastT = t; frames++;
  if (frames > 30 && dt > 0.05) { if (++slowFrames > 25 && quality > 0) { degrade(); slowFrames = 0; } } else if (dt < 0.02) slowFrames = Math.max(0, slowFrames - 1);
  if (DEBUG && frames % 10 === 0) tagEl.textContent = `${(1 / Math.max(dt, 1e-3)).toFixed(0)} fps · q${quality} · ${renderer.info.render.triangles} tris · ${renderer.info.programs.length} programs`;
  const xScale = THREE.MathUtils.clamp((W / H) / 1.5, 0.3, 1);
  lamp.position.x = s.lampX * xScale;
  target.set(lamp.position.x + s.camX + s.offX * xScale, s.lookY + s.explode * 0.35, 0);
  camera.position.set(target.x + Math.sin(s.camAngle) * s.camDist, s.camHeight + s.explode * 0.4, Math.cos(s.camAngle) * s.camDist);
  camera.lookAt(target);

  /* LED colour + output. Warm LED: 5–10 W. RGB LED: 1–3 W, so roughly a third of the light. */
  if (cycling) { cycleHue = (cycleHue + 0.0009) % 1; rgbCol.setHSL(cycleHue, 1, 0.55); }
  cur.copy(warmCol).lerp(rgbCol, s.rgb);
  const outMul = 1 - s.rgb * 0.68;
  const glowCol = tmpC.copy(cur);

  // shelf glow: nearest lamp to the camera focus is lit, the others keep a faint glow
  const near = (x) => 1 - THREE.MathUtils.clamp(Math.abs(s.camX - x) / (SPACING * 0.62), 0, 1);
  const shojiGlow = s.shelf > 0.01 ? 0.12 + 0.88 * near(0) : 1;
  let best = null, bestG = 0;
  for (const o of shelf) {
    const gl = s.shelf * (0.1 + 0.9 * near(o.x)) * (1 - s.roomDim * 0.6) * (o.light.rgb ? outMul : 1);
    o.glowMats.forEach(m => { m.emissiveIntensity = gl * (o.light.rgb ? 1 - s.rgb * 0.25 : 1); if (o.light.rgb) m.emissive.copy(glowCol); });
    if (gl > bestG) { bestG = gl; best = o; }
  }
  if (best && s.shelf > 0.01 && Math.abs(s.camX) > 0.3) { shelfLight.position.set(best.x + best.light.position.x, best.light.position.y, 0); shelfLight.color.copy(best.light.rgb ? cur : best.light.color); shelfLight.intensity = bestG * 9; } else shelfLight.intensity = 0;
  if (s.shelf > 0.5) {
    const i = Math.round((s.camX - shelfX[ORDER[0]]) / SPACING);
    const idx = THREE.MathUtils.clamp(i, 0, PRODUCTS.length - 1);
    if (idx !== curIdx) { curIdx = idx; slideEls.forEach((el, k) => el.classList.toggle('on', k === idx)); dotEls.forEach((el, k) => el.classList.toggle('on', k === idx)); pidx.textContent = String(idx + 1).padStart(2, '0'); }
  }

  const catching = s.power > 0.02 && s.power < 0.98 ? (1 - s.power) : 0;
  const flick = 1 + (Math.sin(t * 47) * Math.sin(t * 13.3) * 0.22 * catching) + Math.sin(t * 1.7) * 0.008;
  const p = Math.max(0, s.power * flick) * shojiGlow;
  led.color.copy(cur); fill.color.copy(cur);
  led.intensity = p * 10 * outMul * (1 - s.roomDim * 0.15);
  fill.intensity = p * 2.2 * outMul;
  paperMat.emissive.copy(cur); paperMat.emissiveIntensity = p * (1.0 - s.rgb * 0.5);
  ledMat.emissive.copy(cur); ledMat.emissiveIntensity = p * 1.8 * outMul;
  led.getWorldPosition(paperMat.userData.u.uLed.value);
  bloom.strength = Math.max(p * outMul, s.shelf * 0.7) * 0.4 + s.workLight * 0.2;
  work.intensity = s.workLight * 110 + s.explode * 28;
  hemi.intensity = (0.12 + s.shelf * 0.3) * (1 - s.roomDim * 0.8);
  moon.intensity = 0.3 * (1 - s.roomDim * 0.7);
  keyLight.intensity = (0.22 + s.shelf * 0.18 + s.power * 0.1) * (1 - s.roomDim * 0.8);
  keyLight.position.set(camera.position.x + 1.2, camera.position.y + 2.2, camera.position.z + 0.6); keyLight.target.position.copy(target);
  mouseLight.intensity = mouse.active ? 0.45 * (1 - s.roomDim) : 0;
  if (mouse.active) mouseLight.position.set(target.x + mouse.x * 3.2, 1.2 + mouse.y * 1.4, 1.6);

  const e = s.explode;
  for (const o of parts) o.position.lerpVectors(o.userData.home, o.userData.out, e);

  clipPlane.constant = s.clipY;
  const printing = s.workLight > 0.02 && s.clipY > -0.05 && s.clipY < PRINT_TOP - 0.02;
  nozzle.visible = printing; ring.visible = printing;
  if (printing) {
    const [nx, nz] = nozzlePos(s.nozzle, RING - 0.02);
    nozzle.position.set(lamp.position.x + nx, s.clipY + 0.02, nz);
    ring.position.set(lamp.position.x, s.clipY + 0.005, 0);
    ring.material.opacity = 0.5 + Math.sin(t * 30) * 0.3;
  }
  const pp = THREE.MathUtils.clamp(s.clipY / PRINT_TOP, 0, 1);
  if (s.workLight > 0.02 || pp < 1) {
    const layer = Math.round(pp * 1240), mins = Math.round(pp * 580);
    layerEl.textContent = String(layer).padStart(4, '0');
    ptimeEl.textContent = `${String(Math.floor(mins / 60)).padStart(2, '0')}H ${String(mins % 60).padStart(2, '0')}M`;
  }

  const lv = THREE.MathUtils.clamp((e - 0.55) / 0.45, 0, 1);
  for (const l of labels) {
    const obj = ref[l.dataset.part]; if (!obj) continue;
    project(obj, tmp2);
    const side = l.classList.contains('r') ? 1 : -1;
    const lx = tmp2.x + side * (isMobile ? 110 : 170), ly = tmp2.y;
    l.style.opacity = lv; l.style.transform = `translate(${lx}px,${ly}px) translate(-50%,-50%)`;
    l._line.setAttribute('x1', tmp2.x); l._line.setAttribute('y1', tmp2.y);
    l._line.setAttribute('x2', lx - side * (isMobile ? 52 : 80)); l._line.setAttribute('y2', ly);
    l._line.style.opacity = lv; l._dot.style.opacity = lv;
    l._dot.setAttribute('cx', tmp2.x); l._dot.setAttribute('cy', tmp2.y);
  }

  project(led, tmp2);
  root.style.setProperty('--lx', tmp2.x.toFixed(0) + 'px');
  root.style.setProperty('--ly', tmp2.y.toFixed(0) + 'px');
  const litv = Math.round(s.power * 100) / 100;
  if (litv !== lastLit) { lastLit = litv; root.style.setProperty('--lit', litv); prog.style.strokeDashoffset = 138.2 * (1 - litv); }
  root.style.setProperty('--glow', '#' + cur.getHexString());

  composer.render();
}
precompile.then(tick);

let rto = null;
function onResize() {
  if (innerWidth < 50 || innerHeight < 50) return;
  if (innerWidth === W && innerHeight === H) return;
  W = innerWidth; H = innerHeight;
  camera.aspect = W / H; camera.updateProjectionMatrix();
  renderer.setSize(W, H); composer.setSize(W, H); bloom.resolution.set(W / 2, H / 2);
  clearTimeout(rto); rto = setTimeout(() => { buildTimeline(); ScrollTrigger.refresh(); }, 200);
}
addEventListener('resize', onResize);
new ResizeObserver(onResize).observe(document.documentElement);
} /* main */
